import {
  COVER_BANNER_DISMISSAL_KEY,
  COVER_BANNER_HIDDEN_DAYS,
} from "../constants";

const HIDDEN_MS = COVER_BANNER_HIDDEN_DAYS * 24 * 60 * 60 * 1000;

/**
 * Closed in this session by a browser that would not remember it (private mode, blocked
 * storage — B8): the close still holds on every page of the layout until the app is
 * reloaded. Set only when the write fails — a remembered close is read back from storage
 * like any other. Module-level and not a context, because the profile layout mounts a new
 * `LayoutProvider` on every route.
 */
let dismissedThisSession = false;

/**
 * Whether the cover banner of the profile layout was closed in this browser in the last
 * 30 days (B4, B7). **Synchronous**, so a closed banner is never rendered and then removed
 * — it would flash, and its image would download (B5).
 *
 * In doubt it is «never closed» (EN-FB1, EN-FB2): storage that throws, a value that does
 * not parse, a `dismissedAt` that is not a number or is in the future. Hiding the banner
 * by mistake would take exposure from the partner that nobody asked to take.
 */
export const isCoverBannerDismissed = (now: Date): boolean => {
  if (dismissedThisSession) return true;
  try {
    const raw = localStorage.getItem(COVER_BANNER_DISMISSAL_KEY);
    if (!raw) return false;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return false;
    const { dismissedAt } = parsed as { dismissedAt?: unknown };
    if (typeof dismissedAt !== "number" || !Number.isFinite(dismissedAt))
      return false;
    const elapsed = now.getTime() - dismissedAt;
    return elapsed >= 0 && elapsed < HIDDEN_MS;
  } catch {
    return false;
  }
};

/**
 * Remembers the close for 30 days, or — if the browser does not allow it — for the session
 * (B8). A new close starts the 30 days again (B7).
 */
export const dismissCoverBanner = (now: Date): void => {
  try {
    localStorage.setItem(
      COVER_BANNER_DISMISSAL_KEY,
      JSON.stringify({ dismissedAt: now.getTime() }),
    );
  } catch {
    dismissedThisSession = true;
  }
};
